export const ACTION_LABELS: Record<string, string> = {
  issue_units: "Issue units / shares",
  transfer_units: "Transfer units / shares",
  update_unit_price: "Update unit price",
  send_announcement: "Send announcements",
  confirm_distribution: "Confirm distribution",
  publish_tax_rules: "Publish tax rules",
  update_investor_bank_accounts: "Update investor bank accounts",
  update_fund_bank_accounts: "Update fund bank accounts",
  approve_redemption_in_principle: "Approve redemption in principle",
  approve_subscription_in_principle: "Approve subscription in principle",
  update_accrual_rate: "Update accrual rate",
  update_accrual_rounding: "Change yield rounding method",
};

// Dual-wording labels resolve to the company's registry wording at render time.
// Replace the longer "units / shares" token first so it is not partially matched as "unit / share".
export const resolveRegistryWording = (label: string, unitRegistry: boolean): string =>
  label
    .replace("units / shares", unitRegistry ? "units" : "shares")
    .replace("unit / share", unitRegistry ? "unit" : "share");

export const resolveActionLabel = (actionType: string, unitRegistry: boolean): string =>
  resolveRegistryWording(ACTION_LABELS[actionType] ?? actionType.replace(/_/g, " "), unitRegistry);

export const ACTION_DESCS: Record<string, string> = {
  issue_units: "Minting new units to an investor's wallet",
  transfer_units: "Moving units between verified holders",
  update_unit_price: "Setting a new NAV-based unit price",
  send_announcement: "Publishing announcements to all investors",
  publish_tax_rules: "Publishing a new tax withholding rule set",
  update_investor_bank_accounts: "Changing an investor's bank account details from the admin profile",
  update_fund_bank_accounts: "Changing subscription or distribution settlement bank details",
  approve_redemption_in_principle: "Committing a redemption request to its dealing day",
  approve_subscription_in_principle: "Committing a subscription application to its dealing window",
  update_accrual_rate: "Declaring a new accrual rate for a unit class",
  update_accrual_rounding: "Changing how a yield fund rounds each day's accrued units",
};

// A route may contain the `:actionRef` placeholder when the action's screen is per-record rather
// than a single shared screen — resubmitUrl fills it from the rejected row's actionRef. reviewUrl
// falls back to the Approvals page for an action with no entry in REVIEW_ACTION_ROUTES, or whose
// row carries no actionRef.
export const ACTION_ROUTES: Record<string, string> = {
  update_unit_price: "company-information/share-price",
  issue_units: "shares-register/issue-share",
  transfer_units: "shares-register/transfer-shares",
  send_announcement: "investor-relations/announcements",
  confirm_distribution: "distributions/:actionRef/edit",
  publish_tax_rules: "tax-rules",
  update_investor_bank_accounts: "register/investor-holdings/:actionRef",
  update_fund_bank_accounts: "settings/bank-accounts",
  approve_redemption_in_principle: "redemptions/:actionRef",
  approve_subscription_in_principle: "subscriptions/:actionRef",
  update_accrual_rate: "company-information/share-price",
  update_accrual_rounding: "settings/yield",
};

// Read-only review routes for pending approvals, keyed the same way as ACTION_ROUTES but kept
// separate: confirm_distribution's edit route and its review route are different pages.
export const REVIEW_ACTION_ROUTES: Record<string, string> = {
  confirm_distribution: "distributions/:actionRef/review",
  update_investor_bank_accounts: "register/investor-holdings/:actionRef",
  approve_redemption_in_principle: "redemptions/:actionRef",
  approve_subscription_in_principle: "subscriptions/:actionRef",
  update_accrual_rate: "company-information/share-price",
  update_accrual_rounding: "settings/yield",
};

export const APPROVAL_COUNT_CHANGED_EVENT = "approval-count-changed";
